import { useMemo, useState } from "react";
import htmldiff from "node-htmldiff";
import {
  Download,
  FileText,
  GitCompareArrows,
  Loader2,
  Pencil,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "@/components/ui/sonner";
import { useAppStore } from "@/store/app";
import {
  letterDocxUrl,
  letterPdfUrl,
  useManualSave,
  useRevisionDiff,
} from "@/hooks/api";
import { LetterEditor } from "./LetterEditor";

// Fetch a binary response and trigger a browser download. Lets us surface
// a spinner on the originating button while the request is in flight —
// the DOCX endpoint in particular makes an LLM call and takes 3-5s, so
// the old plain `<a href download>` looked frozen.
async function downloadFile(
  url: string,
  filename: string,
  setLoading: (b: boolean) => void,
): Promise<void> {
  setLoading(true);
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    toast.error(`Download failed: ${msg}`);
  } finally {
    setLoading(false);
  }
}

export function LetterPreview() {
  const letterHtml = useAppStore((s) => s.letterHtml);
  const appealType = useAppStore((s) => s.appealType);
  const sessionId = useAppStore((s) => s.sessionId);
  const letterMode = useAppStore((s) => s.letterMode);
  const setLetterMode = useAppStore((s) => s.setLetterMode);
  const diffingRevisionId = useAppStore((s) => s.diffingRevisionId);
  const setDiffingRevision = useAppStore((s) => s.setDiffingRevision);
  const revisions = useAppStore((s) => s.revisions);
  const currentRevisionId = useAppStore((s) => s.currentRevisionId);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [docxDownloading, setDocxDownloading] = useState(false);

  if (!letterHtml) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        No letter yet.
      </div>
    );
  }

  const versionIndex = useMemo(
    () => revisions.findIndex((r) => r.id === currentRevisionId),
    [revisions, currentRevisionId],
  );
  const versionLabel = versionIndex >= 0 ? `v${versionIndex + 1}` : null;
  const currentRevision = revisions[versionIndex] ?? null;
  const canDiff = !!currentRevision?.parent_revision_id;

  // Letter / Changes toggle. "Editing" is a separate mode entered via
  // the Edit button — keeps the toggle a binary on/off for diff.
  const toggleValue = letterMode === "diff" ? "diff" : "letter";
  function onToggleChange(v: string) {
    if (!v) return; // radix calls with "" on de-select
    if (v === "diff") {
      if (currentRevision?.id) setDiffingRevision(currentRevision.id);
    } else {
      setDiffingRevision(null);
    }
  }

  return (
    <main className="bg-slate-50 overflow-y-auto">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold">Appeal Letter</span>
              {appealType && letterMode !== "editing" && (
                <Badge variant="emerald" className="font-mono text-[10px]">
                  {appealType}
                </Badge>
              )}
              {versionLabel && letterMode !== "editing" && (
                <Badge
                  variant="outline"
                  className="font-mono text-[10px] border-slate-300 bg-slate-50 text-slate-700"
                >
                  {versionLabel}
                </Badge>
              )}
              {letterMode === "editing" && (
                <Badge
                  variant="outline"
                  className="font-mono text-[10px] border-amber-300 bg-amber-50 text-amber-800"
                >
                  Editing {versionLabel}
                </Badge>
              )}
            </div>
            {letterMode !== "letter" && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {letterMode === "editing"
                  ? "Edit directly, then click Save to record a new revision."
                  : "Tracked changes from the prior revision."}
              </div>
            )}
          </div>

          {sessionId && letterMode !== "editing" && (
            <div className="flex items-center gap-2">
              <ToggleGroup
                type="single"
                value={toggleValue}
                onValueChange={onToggleChange}
                disabled={!canDiff}
              >
                <ToggleGroupItem value="letter" className="gap-1.5 text-xs h-8">
                  <FileText className="w-3.5 h-3.5" />
                  Letter
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="diff"
                  className="gap-1.5 text-xs h-8"
                  disabled={!canDiff}
                  title={canDiff ? "Show changes from the previous revision" : "No prior revision to compare"}
                >
                  <GitCompareArrows className="w-3.5 h-3.5" />
                  Changes
                </ToggleGroupItem>
              </ToggleGroup>
              <Button
                variant="outline"
                className="gap-1.5"
                size="sm"
                onClick={() => setLetterMode("editing")}
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </Button>
              <Button
                className="gap-1.5"
                size="sm"
                disabled={pdfDownloading}
                onClick={() =>
                  downloadFile(
                    letterPdfUrl(sessionId),
                    "appeal_letter.pdf",
                    setPdfDownloading,
                  )
                }
              >
                {pdfDownloading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                PDF
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                size="sm"
                disabled={docxDownloading}
                onClick={() =>
                  downloadFile(
                    letterDocxUrl(sessionId),
                    "appeal_letter.docx",
                    setDocxDownloading,
                  )
                }
                title={
                  docxDownloading
                    ? "Formatting DOCX… (takes a few seconds)"
                    : "Download as Word document"
                }
              >
                {docxDownloading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                DOCX
              </Button>
            </div>
          )}
        </div>

        {letterMode === "letter" && <LetterIframe html={letterHtml} />}
        {letterMode === "diff" && diffingRevisionId && (
          <DiffIframe sessionId={sessionId} revisionId={diffingRevisionId} />
        )}
        {letterMode === "editing" && sessionId && (
          <EditorPane sessionId={sessionId} initialHtml={letterHtml} />
        )}
      </div>
    </main>
  );
}

function LetterIframe({ html }: { html: string }) {
  return (
    <div className="bg-white border shadow-sm rounded-lg">
      <iframe
        title="Appeal letter preview"
        srcDoc={html}
        className="w-full rounded-lg"
        style={{ height: "calc(100vh - var(--chrome-h, 56px) - 124px)" }}
      />
    </div>
  );
}

// Style sheet overlaid on the letter HTML to surface htmldiff's <ins>
// and <del> spans. Tracked-changes look: red strikethrough for removed,
// green underline for inserted. Inherits all of the letter's own
// typography so the rest of the page is visually identical.
const DIFF_OVERLAY_CSS = `
ins { background-color: #dcfce7; color: #14532d; text-decoration: none; border-bottom: 1px solid #86efac; padding: 0 1px; }
del { background-color: #fee2e2; color: #7f1d1d; text-decoration: line-through; text-decoration-color: #fca5a5; padding: 0 1px; }
ins img, del img { outline: 2px solid currentColor; }
`;

function injectDiffStyles(html: string): string {
  // Append our overlay <style> just before </head>; fall back to
  // wrapping if the letter doesn't have a head (unlikely but safe).
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `<style>${DIFF_OVERLAY_CSS}</style></head>`);
  }
  return `<!doctype html><html><head><style>${DIFF_OVERLAY_CSS}</style></head><body>${html}</body></html>`;
}

function DiffIframe({
  sessionId,
  revisionId,
}: {
  sessionId: string | null;
  revisionId: string;
}) {
  const { data, isLoading, error } = useRevisionDiff(sessionId, revisionId);

  const diffDoc = useMemo(() => {
    if (!data) return null;
    const merged = htmldiff(data.old_html, data.new_html);
    return injectDiffStyles(merged);
  }, [data]);

  if (isLoading) {
    return (
      <div className="bg-white border shadow-sm rounded-lg p-12 text-sm text-muted-foreground flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Computing changes…
      </div>
    );
  }
  if (error || !diffDoc) {
    return (
      <div className="bg-white border shadow-sm rounded-lg p-12 text-sm text-red-700">
        Failed to load diff.
      </div>
    );
  }
  return (
    <div className="bg-white border shadow-sm rounded-lg">
      <iframe
        title="Revision diff"
        srcDoc={diffDoc}
        className="w-full rounded-lg"
        style={{ height: "calc(100vh - var(--chrome-h, 56px) - 124px)" }}
      />
    </div>
  );
}

function EditorPane({
  sessionId,
  initialHtml,
}: {
  sessionId: string;
  initialHtml: string;
}) {
  const setLetterMode = useAppStore((s) => s.setLetterMode);
  const appendRefinementMessage = useAppStore((s) => s.appendRefinementMessage);
  const appendRevision = useAppStore((s) => s.appendRevision);
  const setCurrentRevision = useAppStore((s) => s.setCurrentRevision);
  const { manualSave, isPending } = useManualSave();

  async function onSave(editedHtml: string) {
    try {
      const rev = await manualSave({ sessionId, html: editedHtml });
      appendRevision(rev);
      setCurrentRevision(rev.id, editedHtml);
      appendRefinementMessage({
        role: "assistant",
        content: rev.change_summary ?? "Manual edit",
        at: new Date().toISOString(),
        revisionId: rev.id,
      });
      setLetterMode("letter");
      toast.success("Saved");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Save failed: ${msg}`);
    }
  }

  return (
    <LetterEditor
      initialHtml={initialHtml}
      onSave={onSave}
      onCancel={() => setLetterMode("letter")}
      saving={isPending}
    />
  );
}
